// How the portal files and counts a finding -- ONE rule for the Overview, Compliance and
// Health tabs, so they can never disagree about where a finding lives or what its number is.

// A compliance finding carries a compliance rule code; the server sends that as
// is_compliance (#367, derived in security/portal_visibility.is_compliance). Words in the
// title are not evidence: "exempt but paid hourly" has no "flsa" in it, and a "tax"
// configuration finding is not a legal one.
export const isComplianceFinding = (obs) => obs?.is_compliance === true

// A finding's status (findings status v2). Active = still being worked: open,
// acknowledged, fix submitted. Resolved (only a scan resolves) and dismissed are closed.
// 'immaterial' is the legacy name of dismissed.
export const STATUS_LABEL = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  fix_submitted: 'Fix submitted',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
  immaterial: 'Dismissed',
}
export const ACTIVE_STATUSES = ['open', 'acknowledged', 'fix_submitted']
export const statusOf = (obs) => (obs?.status === 'immaterial' ? 'dismissed' : (obs?.status || 'open'))
export const isActive = (obs) => ACTIVE_STATUSES.includes(statusOf(obs))
export const isClosed = (obs) => !isActive(obs)

// Which findings tab a finding lives on -- the ONE partition. A finding is on exactly one.
export const FINDING_TABS = {
  compliance: isComplianceFinding,
  health: (obs) => !isComplianceFinding(obs),
}
export const findingTabOf = (obs) => (isComplianceFinding(obs) ? 'compliance' : 'health')

// An FYI finding: its check's observation type is 'informational' (the server sends
// observation_type; services/finding_status.is_informational). It is listed, labelled FYI,
// and never counted -- not in a tier, the "Open" filter, a badge or a total -- whatever
// its severity.
export const isInformational = (obs) => obs?.observation_type === 'informational'

// The tier a finding is listed and counted at: 'critical' (risk), 'warning' (insight),
// 'info' (note), or 'fyi' (informational, at any severity); null for none of these.
export const tierOf = (obs) => {
  if (isInformational(obs)) return 'fyi'
  return { risk: 'critical', insight: 'warning', note: 'info' }[obs?.type] || null
}

// The tiers the "Open" filter and the badge count: Critical (risk) and Warning (insight).
export const isOpenTier = (obs) => tierOf(obs) === 'critical' || tierOf(obs) === 'warning'

// The numbers a findings tab shows, from the findings the page was handed. The tab's
// summary, its "Open N" chip, the tab-bar badge (staff page and portal) and the Overview
// all read this, so none of them can count a different set. (The badges used to count
// every finding's tier across BOTH tabs -- the Compliance badge every active risk, the
// Health badge every active risk and insight -- so neither footed to its tab.)
//   critical / warning / info: active findings on this tab by tier (risk / insight / note)
//   fyi: active FYI findings (isInformational) -- listed, counted in none of the above
//   open: critical + warning (isOpenTier) -- the tab's "Open" filter, and its badge
//   active: the active findings themselves (open, acknowledged, fix submitted), FYI included
// Resolved and dismissed findings are never counted here; the tabs list them from the
// closed findings they read separately.
export function tabFindingCounts(observations, tab) {
  const onTab = FINDING_TABS[tab]
  if (!onTab) throw new Error(`unknown findings tab: ${tab}`)
  const active = (observations || []).filter(o => onTab(o) && isActive(o))
  const critical = active.filter(o => tierOf(o) === 'critical').length
  const warning = active.filter(o => tierOf(o) === 'warning').length
  const info = active.filter(o => tierOf(o) === 'info').length
  const fyi = active.filter(o => tierOf(o) === 'fyi').length
  return { critical, warning, info, fyi, open: active.filter(isOpenTier).length, active }
}

// The badge a tab carries in the tab bar: its open count, or 0 for a tab that lists no findings.
export const tabBadgeCount = (observations, tabKey) =>
  (FINDING_TABS[tabKey] ? tabFindingCounts(observations, tabKey).open : 0)

// The number a finding shows, said as what it counts: the check's reviewed subject
// ("12 PTO plans") when it has one; else distinct people when the scan counted them;
// otherwise the check's record count, said as records. Null when there is no count.
export function findingCount(obs) {
  if (!obs) return null
  if (obs.subject_count > 0 && obs.subject_noun) {
    return { value: obs.subject_count, label: obs.subject_count === 1 ? obs.subject_noun_one : obs.subject_noun }
  }
  const people = obs.people_count ?? obs.affected_count
  if (people > 0) return { value: people, label: people === 1 ? 'person' : 'people' }
  if (obs.record_count > 0) return { value: obs.record_count, label: obs.record_count === 1 ? 'record' : 'records' }
  return null
}

// The group a finding is listed under: its check library's own category, exactly as the
// library writes it -- the server sends it as `category` (security/portal_visibility
// .library_category; Scott, 2026-10-08). Never a heading of our own, never a guess from
// words in the title. A finding that arrives without one is listed as Uncategorized.
export const UNCATEGORIZED = 'Uncategorized'
export const findingCategory = (obs) =>
  (typeof obs?.category === 'string' && obs.category.trim() ? obs.category : UNCATEGORIZED)

// Findings grouped by findingCategory, as [category, findings] pairs: categories in
// alphabetical order, Uncategorized last; findings keep the order they were given in.
// Every findings tab lists its groups through this, so no tab can group differently.
export function groupByCategory(findings) {
  const groups = new Map()
  for (const obs of findings || []) {
    const c = findingCategory(obs)
    if (!groups.has(c)) groups.set(c, [])
    groups.get(c).push(obs)
  }
  return [...groups.keys()]
    .sort((a, b) => ((a === UNCATEGORIZED) - (b === UNCATEGORIZED)) || a.localeCompare(b, 'en'))
    .map(c => [c, groups.get(c)])
}
